import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';
import { isApiError } from '../lib/api';
import { MIN_PASSWORD_LENGTH, checkResetToken, resetPassword } from '../lib/auth-api';
import { useAgentStore } from '../stores/agent';
import {
  Particles,
  ClickSpark,
  AnimatedContent,
} from '../components/react-bits/index.js';
import styles from './Login.module.css';

// Landing page for the emailed link: /#/reset-password?token=…
type Phase = 'checking' | 'ready' | 'invalid' | 'done';

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const setAuthMessage = useAgentStore((s) => s.setAuthMessage);

  const [phase, setPhase] = useState<Phase>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      return;
    }
    let ignore = false;
    checkResetToken(token)
      .then((valid) => {
        if (!ignore) setPhase(valid ? 'ready' : 'invalid');
      })
      .catch(() => {
        if (!ignore) setPhase('ready');
      });
    return () => {
      ignore = true;
    };
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (password !== confirm) {
      setError('The two passwords don’t match.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await resetPassword(token, password);
      setPhase('done');
      setAuthMessage('Password changed. Sign in with your new password.');
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      if (isApiError(err) && err.status === 400) {
        const body = err.body as { error?: string } | null;
        setError(
          body?.error === 'invalid-token'
            ? 'This link has expired or was already used. Request a new one.'
            : `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
        );
      } else if (isApiError(err) && err.status === 403) {
        setError('This account is disabled. Contact an administrator.');
      } else if (isApiError(err) && err.status === 429) {
        setError('Too many attempts. Wait a few minutes and try again.');
      } else {
        setError('Could not reach the server. Try again in a moment.');
      }
      setSubmitting(false);
    }
  }

  return (
    <ClickSpark sparkColor="#0979f5" sparkSize={10} sparkRadius={22} sparkCount={10}>
      <main className={styles.root}>
        <div className={styles.studioGlow} aria-hidden />

        <div className={styles.loginBg} aria-hidden>
          <Particles
            particleCount={45}
            particleSpread={12}
            speed={0.1}
            particleColors={['#0979f5', '#0499f9', '#5d39f5', '#60a5fa']}
            moveParticlesOnHover={true}
            alphaParticles={true}
            particleBaseSize={100}
            disableRotation={false}
          />
        </div>

        <AnimatedContent
          trigger="mount"
          distance={20}
          duration={0.6}
          ease="power3.out"
          className={styles.authCard}
        >
          {/* Top Brand Header */}
          <div className={styles.topBrandRow}>
            <BrandLogo variant="wordmark" height={28} />
            <div className={styles.brandPill}>
              <span className={styles.brandPillDot} />
              <span>SECURITY & CREDENTIALS</span>
            </div>
          </div>

          {/* Title & Subtitle */}
          <div className={styles.headerBlock}>
            <h1 className={styles.title}>Choose a new password</h1>
            <p className={styles.subtitle}>
              At least {MIN_PASSWORD_LENGTH} characters. Setting a new password signs out every device currently using this account.
            </p>
          </div>

          {phase === 'checking' && (
            <p className={styles.subtitle} style={{ padding: '12px 0' }}>
              Checking your reset link…
            </p>
          )}

          {phase === 'invalid' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#ef4444' }}>
                <AlertCircle size={22} />
                <span style={{ fontSize: 15, fontWeight: 700 }}>Invalid or expired link</span>
              </div>
              <p className={styles.subtitle} style={{ lineHeight: 1.6 }}>
                This reset link has expired, was already used, or isn&rsquo;t valid. Links last an hour and work once.
              </p>
              <div style={{ marginTop: 8 }}>
                <Link to="/forgot-password" className={styles.submitBtn} style={{ textDecoration: 'none' }}>
                  <span>Request a new link</span>
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
              <div className={styles.footerRow} style={{ justifyContent: 'center', marginTop: 8 }}>
                <Link to="/login" className={styles.footerLink}>
                  &larr; Back to sign in
                </Link>
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#10b981' }}>
                <CheckCircle2 size={22} />
                <span style={{ fontSize: 15, fontWeight: 700 }}>Password updated</span>
              </div>
              <p className={styles.subtitle}>
                Password changed successfully. Taking you to the workstation login…
              </p>
            </div>
          )}

          {phase === 'ready' && (
            <>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.field}>
                  <label htmlFor="new-password" className={styles.label}>
                    New password
                  </label>
                  <div className={styles.passwordWrap}>
                    <input
                      id="new-password"
                      className={styles.input}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      autoFocus
                      minLength={MIN_PASSWORD_LENGTH}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={submitting}
                      placeholder="••••••••••••"
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className={styles.field}>
                  <label htmlFor="confirm-password" className={styles.label}>
                    Confirm new password
                  </label>
                  <div className={styles.passwordWrap}>
                    <input
                      id="confirm-password"
                      className={styles.input}
                      type={showConfirm ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      minLength={MIN_PASSWORD_LENGTH}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      disabled={submitting}
                      placeholder="••••••••••••"
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowConfirm(!showConfirm)}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    >
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className={styles.error} role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className={styles.submitBtn}
                >
                  <span>{submitting ? 'Saving password…' : 'Set new password'}</span>
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              </form>

              <div className={styles.footerRow} style={{ justifyContent: 'center' }}>
                <Link to="/login" className={styles.footerLink}>
                  &larr; Back to sign in
                </Link>
              </div>
            </>
          )}
        </AnimatedContent>
      </main>
    </ClickSpark>
  );
}
